import { toast } from "sonner";
import { ensureShareSlug, regenerateShareSlug, revokeShareSlug, type SavedItinerary } from "./saved-itineraries";
import { buildRouteShareUrl } from "./share-url";

/** Generates/reuses a share slug and copies the link to the clipboard. */
export async function shareItinerary(it: Pick<SavedItinerary, "id" | "share_slug" | "title">) {
  try {
    const slug = await ensureShareSlug(it.id, it.share_slug);
    // Domínio canónico: o hostname do Preview exige login da plataforma.
    const url = buildRouteShareUrl(slug);


    // Partilha nativa apenas com o URL: com `text` livre, várias apps colam
    // texto em vez de abrirem a ligação (acabava em pesquisa do Google).
    const navAny = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (navAny.share) {
      try {
        await navAny.share({ title: it.title, url });
        return slug;
      } catch {
        // user cancelled or unsupported — fall back to copy
      }
    }

    await copyToClipboard(url);
    toast.success("Link copiado!", { description: url });
    return slug;
  } catch (e) {
    toast.error((e as Error).message ?? "Erro a criar link");
    throw e;
  }
}

/** Revokes a share link so the old URL stops working (owner-only, via RLS). */
export async function revokeShareLink(id: string): Promise<void> {
  try {
    await revokeShareSlug(id);
    toast.success("Link revogado — deixou de funcionar.");
  } catch (e) {
    toast.error((e as Error).message ?? "Erro a revogar o link");
    throw e;
  }
}

/** Rotates the share token: old link stops working, a new one is copied. */
export async function regenerateShareLink(
  it: Pick<SavedItinerary, "id" | "title">,
): Promise<string> {
  try {
    const slug = await regenerateShareSlug(it.id);
    const url = buildRouteShareUrl(slug);
    await copyToClipboard(url).catch(() => {});
    toast.success("Novo link gerado — o anterior deixou de funcionar.", { description: url });
    return slug;
  } catch (e) {
    toast.error((e as Error).message ?? "Erro a gerar novo link");
    throw e;
  }
}

/** Copia um URL com fallback para contextos sem Clipboard API. */
async function copyToClipboard(url: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return;
    }
  } catch {
    /* segue para o fallback */
  }
  const el = document.createElement("textarea");
  el.value = url;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(el);
  }
}

/** Copia explicitamente a ligação pública (sem abrir a folha de partilha). */
export async function copyShareLink(it: Pick<SavedItinerary, "id" | "share_slug">): Promise<string> {
  const slug = await ensureShareSlug(it.id, it.share_slug);
  const url = buildRouteShareUrl(slug);
  await copyToClipboard(url);
  toast.success("Link copiado!", { description: url });
  return slug;
}
